import React, { useEffect, useRef } from 'react';
import "./InfiniteScroll.scss";

interface InfiniteScrollProps {
  children: React.ReactElement[];
}

export const InfiniteScroll: React.FC<InfiniteScrollProps> = ({ children }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const translation = useRef<number>(0);
  const totalWidth = useRef<number>(0);

  // Recalculate width of one full loop (only original children)
  useEffect(() => {
    const computeTotalWidth = () => {
      if (!containerRef.current) return;
      const items = Array.from(containerRef.current.children) as HTMLElement[];
      const half = items.length / 2;
      let width = 0;
      for (let i = 0; i < half; i++) {
        width += items[i].offsetWidth + 30; // gap
      }
      totalWidth.current = width;
    };

    computeTotalWidth();
    const resizeObserver = new ResizeObserver(computeTotalWidth);
    if (containerRef.current) resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, [children]);

  // Auto-scroll at a constant speed (no user interaction)
  useEffect(() => {
    const interval = setInterval(() => {
      const container = containerRef.current;
      if (!container || totalWidth.current === 0) return;

      const screenW = window.innerWidth;
      const speedMultiplier = Math.min(5, Math.max(1, 1920 / screenW));
      const baseSpeed = 0.00075 * container.offsetWidth * speedMultiplier;

      translation.current =
        (translation.current + baseSpeed) % totalWidth.current;
      container.style.transform = `translateX(-${translation.current}px)`;
    }, 10);

    return () => clearInterval(interval);
  }, []);

  // Duplicate children for seamless loop
  const doubledChildren = [...children, ...children];

  return (
    <div className="slider">
      <div ref={containerRef} className="slide-track">
        {doubledChildren.map((child, index) => (
          <div key={index}>{child}</div>
        ))}
      </div>
    </div>
  );
};
